/**
 * DefaultAuthStrategy - Стандартна стратегія авторізації
 * 
 * Реалізує поточну логіку авторізації:
 * 1. POST запит на loginUrl з credentials
 * 2. GET запит на userMeUrl для отримання даних користувача
 * 
 * Використовується за замовчуванням, якщо не вказана інша стратегія
 */

import type {
    IAuthContext,
    IAuthStrategy,
    IAuthStrategyOptions,
    IAuthStrategyResult,
} from '@/Core/Contracts/Auth/IAuthStrategy'
import type { ILoginData, ILoginResponse, IUser } from '@/Core/Contracts/Managers/IUserManager'

/**
 * Стандартна стратегія авторізації
 * 
 * @example
 * ```typescript
 * const strategy = new DefaultAuthStrategy()
 * const result = await strategy.execute(
 *   { login: 'user', password: 'pass' },
 *   context,
 *   { fetchUserData: true }
 * )
 * ```
 */
export class DefaultAuthStrategy implements IAuthStrategy {
    /**
     * Виконує авторізацію користувача
     * 
     * Алгоритм:
     * 1. Відправляє POST запит з credentials
     * 2. Перевіряє наявність токена в відповіді
     * 3. Отримує дані користувача через GET запит
     * 4. Повертає об'єднаний результат
     */
    async execute(
        data: ILoginData,
        context: IAuthContext,
        options?: IAuthStrategyOptions
    ): Promise<IAuthStrategyResult> {
        try {
            // Крок 1: Авторизація
            const loginData = (await context.post<ILoginResponse>(context.loginUrl, {
                json: data,
            })) as unknown as ILoginResponse | null

            if (!loginData) {
                console.error('[DefaultAuthStrategy] No response from server')
                return {
                    success: false,
                    error: 'No response from server',
                    errorType: 'server',
                }
            }

            // Перевіряємо наявність токена
            if (!loginData.token) {
                console.error('[DefaultAuthStrategy] No token in response')
                return {
                    success: false,
                    error: 'Invalid credentials',
                    errorType: 'credentials',
                }
            }

            // Крок 2: Отримання даних користувача (якщо потрібно)
            let userData: IUser | null = null

            if (options?.fetchUserData !== false) {
                try {
                    userData = (await context.get<IUser>(context.userMeUrl, {
                        headers: {
                            Authorization: `Bearer ${loginData.token}`,
                            ...options?.headers,
                        },
                    })) as unknown as IUser | null

                    if (!userData) {
                        console.error('[DefaultAuthStrategy] No user data received')
                        return {
                            success: false,
                            error: 'Failed to fetch user data - no data received',
                            errorType: 'server',
                        }
                    }
                    
                    // Перевіряємо наявність id або uuid (backend може повертати будь-яке з них)
                    const hasIdentifier = (userData.id !== undefined && userData.id !== null) || 
                                        (userData['uuid'] !== undefined && userData['uuid'] !== null) ||
                                        (userData['@id'] !== undefined && userData['@id'] !== null)
                    
                    if (!hasIdentifier) {
                        console.error('[DefaultAuthStrategy] User data missing identifier (id/uuid/@id):', userData)
                        return {
                            success: false,
                            error: 'Failed to fetch user data - missing user identifier',
                            errorType: 'server',
                        }
                    }
                    
                    // Нормалізуємо поле id: використовуємо uuid або @id якщо id відсутній
                    if (!userData.id && userData['uuid']) {
                        userData.id = userData['uuid']
                    } else if (!userData.id && userData['@id']) {
                        userData.id = userData['@id']
                    }
                } catch (error) {
                    console.error('[DefaultAuthStrategy] Error fetching user data:', error)
                    return {
                        success: false,
                        error: `Failed to fetch user data: ${error instanceof Error ? error.message : 'Unknown error'}`,
                        errorType: 'server',
                    }
                }
            }
            
            // Крок 3: Отримання permissions (якщо користувач авторизований)
            try {
                const permissions = await context.get('/services/auth/permissions', {
                    headers: {
                        Authorization: `Bearer ${loginData.token}`,
                        ...options?.headers,
                    },
                })
                
                // Зберігаємо permissions в userData
                if (userData && permissions) {
                    userData['permissions'] = permissions
                }
            } catch (error) {
                console.error('[DefaultAuthStrategy] Failed to fetch permissions:', error)
                // Не критична помилка, продовжуємо авторизацію
            }
            
            // Повертаємо успішний результат
            const result: IAuthStrategyResult = {
                success: true,
                token: loginData.token,
                refreshToken: loginData.refresh_token,
                refreshTokenExpiration: loginData.refresh_token_expiration,
                centrifugoConnectionToken: loginData.centrifugo_connection_token ?? null,
                centrifugoConnectionTokenExpiration: loginData.centrifugo_connection_token_expiration ?? null,
                centrifugoSubscriptionToken: loginData.centrifugo_subscription_token ?? null,
                centrifugoSubscriptionTokenExpiration: loginData.centrifugo_subscription_token_expiration ?? null,
                mustChangePassword: loginData.must_change_password ?? false,
            }

            if (userData) {
                result.user = userData
            }

            return result
        } catch (error) {
            console.error('[DefaultAuthStrategy] Error during authentication:', error)
            // Пытаемся извлечь сообщение от бекенда
            let errorMessage = 'Unknown error'
            
            if (error && typeof error === 'object') {
                // Проверяем response.data.message (стандартный формат API)
                if ('response' in error && error.response && typeof error.response === 'object') {
                    let response: any = null
                    if (typeof (error as any).response.json === 'function') {
                        // Response-об'єкт — тіло ще не читалось (або читалось в RestClientService)
                        try {
                            response = await (error as any).response.json()
                        } catch {
                            // тіло вже спожите або не JSON — ігноруємо
                        }
                    } else {
                        // Вже розібраний plain-об'єкт (RestClientService вже розпарсив)
                        response = (error as any).response
                    }
                    
                    if (response && typeof response === 'object') {
                        if (response.message) {
                            errorMessage = response.message
                        } else if (response.error) {
                            errorMessage = response.error
                        }
                    }
                }
                // Если не нашли в response.data, проверяем error.message
                else if ('message' in error && typeof error.message === 'string') {
                    errorMessage = error.message
                }
            }

            return {
                success: false,
                error: errorMessage,
                errorType: 'server',
            }
        }
    }
}

/**
 * Singleton instance стандартної стратегії
 * Можна використовувати напряму без створення нових екземплярів
 */
export const defaultAuthStrategy = new DefaultAuthStrategy()
